import React, { useEffect, useState } from 'react'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/utils/cn'
import type { Toast as ToastItem, ToastType } from '@/types'

interface ToastConfig {
  icon: React.ReactNode
  containerClass: string
  iconClass: string
  barClass: string
}

function getToastConfig(type: ToastType): ToastConfig {
  switch (type) {
    case 'success':
      return {
        icon: <CheckCircle size={18} />,
        containerClass: 'bg-white dark:bg-slate-800 border-green-200 dark:border-green-800',
        iconClass: 'text-green-500',
        barClass: 'bg-green-500',
      }
    case 'error':
      return {
        icon: <XCircle size={18} />,
        containerClass: 'bg-white dark:bg-slate-800 border-red-200 dark:border-red-800',
        iconClass: 'text-red-500',
        barClass: 'bg-red-500',
      }
    case 'warning':
      return {
        icon: <AlertTriangle size={18} />,
        containerClass: 'bg-white dark:bg-slate-800 border-amber-200 dark:border-amber-800',
        iconClass: 'text-amber-500',
        barClass: 'bg-amber-500',
      }
    case 'info':
    default:
      return {
        icon: <Info size={18} />,
        containerClass: 'bg-white dark:bg-slate-800 border-blue-200 dark:border-blue-800',
        iconClass: 'text-blue-500',
        barClass: 'bg-blue-500',
      }
  }
}

interface ToastItemComponentProps {
  toast: ToastItem
  onRemove: (id: string) => void
}

function ToastItemComponent({ toast, onRemove }: ToastItemComponentProps) {
  const [visible, setVisible] = useState(false)
  const [progress, setProgress] = useState(100)
  const config = getToastConfig(toast.type)

  useEffect(() => {
    // Trigger enter animation
    const enterTimer = requestAnimationFrame(() => {
      setVisible(true)
    })

    return () => cancelAnimationFrame(enterTimer)
  }, [])

  useEffect(() => {
    if (toast.duration <= 0) return

    const startTime = Date.now()
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, 100 - (elapsed / toast.duration) * 100)
      setProgress(remaining)
      if (remaining === 0) clearInterval(interval)
    }, 50)

    return () => clearInterval(interval)
  }, [toast.duration])

  const handleRemove = () => {
    setVisible(false)
    setTimeout(() => onRemove(toast.id), 150)
  }

  return (
    <div
      className={cn(
        'relative overflow-hidden flex items-start gap-3 min-w-[300px] max-w-[400px]',
        'rounded-lg border shadow-lg px-4 py-3',
        'transition-all duration-150',
        config.containerClass,
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
      )}
      role="alert"
      aria-live="polite"
    >
      {/* Icon */}
      <span className={cn('flex-shrink-0 mt-0.5', config.iconClass)}>
        {config.icon}
      </span>

      {/* Message */}
      <p className="flex-1 text-sm text-navy dark:text-slate-200 leading-relaxed">
        {toast.message}
      </p>

      {/* Close button */}
      <button
        onClick={handleRemove}
        className="flex-shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
        aria-label="Dismiss notification"
      >
        <X size={14} />
      </button>

      {/* Progress bar */}
      {toast.duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-100 dark:bg-slate-700">
          <div
            className={cn('h-full transition-all ease-linear', config.barClass)}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  )
}

/**
 * Toast container - renders all active toasts in the top-right corner.
 * Place this once near the root of your app.
 */
export function ToastContainer() {
  const { toasts, removeToast } = useToast()

  if (toasts.length === 0) return null

  return (
    <div
      className="fixed top-4 right-4 z-[9999] flex flex-col gap-2"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItemComponent
          key={toast.id}
          toast={toast}
          onRemove={removeToast}
        />
      ))}
    </div>
  )
}

export default ToastContainer
